import { Center, Loader, MantineColorsTuple, MantineProvider, NumberInput, createTheme } from '@mantine/core';
import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router';
import App from './App';
import AppProvider from './provider/AppContext';
import reportWebVitals from './reportWebVitals';

import BlankLayout from './components/layout/BlankLayout';
import ErrorPage from './components/layout/ErrorPage';
import ProtectedRoute from './components/layout/ProtectedRoute';
import RoleRoute from './components/layout/RoleRoute';
import UnprotectedRoute from './components/layout/UnprotectedRoute';
import { UserRoles } from './models/user';

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/spotlight/styles.css';

import { Notifications } from '@mantine/notifications';
import { DatePickerInput, DatesProvider } from '@mantine/dates';
import 'dayjs/locale/it';
import './index.css';
import Login from './pages/Authentication/Login/Login';

import '../src/extension/extension';

/**
 * Le pagine si caricano al primo accesso alla rotta.
 *
 * Importarle tutte staticamente significava servire l'intera applicazione — incluse le
 * schermate di amministrazione e la libreria PDF — a chi apre solo la home. Restano
 * statiche il layout e il login, che servono comunque subito.
 */
const Home = lazy(() => import('./pages/Home/home'));
const ContractDetailView = lazy(() => import('./pages/Contract/contract-detail-view'));
const ContractSearchView = lazy(() => import('./pages/Contract/contract-search-view'));
const CustomerReportView = lazy(() => import('./pages/CustomerReport/customer-report-view'));
const LoadingOrderSearchView = lazy(() => import('./pages/LoadingOrder/loading-order-search-view'));
const MadDetailView = lazy(() => import('./pages/Mad/mad-detail-view'));
const MadSearchView = lazy(() => import('./pages/Mad/mad-search-view'));
const PriceListView = lazy(() => import('./pages/PriceList/price-list-view'));
const StorageReportView = lazy(() => import('./pages/Report/storage-report-view'));
const ShipView = lazy(() => import('./pages/Ship/ship-view'));
const TaxView = lazy(() => import('./pages/Tax/tax-view'));
const AppSettingView = lazy(() => import('./pages/Admin/Setting/app-setting-view'));
const UserDetailView = lazy(() => import('./pages/Admin/User/user-detail-view'));
const UserSearchView = lazy(() => import('./pages/Admin/User/user-search-view'));
const ChangePassword = lazy(() => import('./pages/Authentication/ChangePassword/ChangePassword'));

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

/** Attesa del chunk della pagina: si vede solo al primo ingresso su una rotta. */
const suspended = (element: React.ReactNode) => (
  <Suspense fallback={<Center h={200}><Loader type="dots" /></Center>}>
    {element}
  </Suspense>
);

const adminOnly = (element: React.ReactNode) =>
  suspended(<RoleRoute roles={[UserRoles.Admin]}>{element}</RoleRoute>);

const router = createBrowserRouter([
  {
    path: "/auth",
    element: <UnprotectedRoute><BlankLayout /></UnprotectedRoute>,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "login",
        element: <Login />
      },
      // La querystring non fa parte del path: "recupero-password?id=:id" non era una
      // rotta valida e non veniva mai raggiunta. L'id si legge con useSearchParams.
      {
        path: "recupero-password",
        element: suspended(<ChangePassword />)
      }
    ],
  },
  {
    path: "/",
    element: <ProtectedRoute><App /></ProtectedRoute>,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/", // <-- here
        element: <Navigate to="/home" replace />
      },
      {
        path: "home",
        element: suspended(<Home />),
      },
      {
        path: "contratti",
        element: suspended(<ContractSearchView />),
      },
      {
        path: "contratti/:id",
        element: suspended(<ContractDetailView />),
      },
      {
        path: "mad",
        element: suspended(<MadSearchView />),
      },
      {
        path: "mad/:id",
        element: suspended(<MadDetailView />),
      },
      {
        path: "buoni",
        element: suspended(<LoadingOrderSearchView />),
      },
      {
        path: "navi",
        element: suspended(<ShipView />),
      },
      {
        path: "listini",
        element: adminOnly(<PriceListView />),
      },
      {
        path: "oneri",
        element: adminOnly(<TaxView />),
      },
      {
        path: "report-magazzinaggio",
        element: suspended(<StorageReportView />),
      },
      {
        path: "riepiloghi",
        element: suspended(<CustomerReportView />),
      },
      {
        path: "admin/impostazioni",
        element: adminOnly(<AppSettingView />),
      },
      {
        path: "admin/utenti",
        element: adminOnly(<UserSearchView />),
      },
      {
        path: "admin/utenti/:id",
        element: adminOnly(<UserDetailView />),
      },
      {
        path: "profilo",
        element: suspended(<UserDetailView />),
      },
    ]
  },
  {
    path: "*",
    element: <ErrorPage />,
  }
]);

const myColor: MantineColorsTuple = [
  '#e2fbff',
  '#cdf2ff',
  '#9ce1ff',
  '#67d1fe',
  '#41c3fd',
  '#2bbafc',
  '#19b5fe',
  '#009fe3',
  '#008dcb',
  '#007ab4'
];

const theme = createTheme({
  /** Put your mantine theme override here */
  colors: { myColor },
  components: {
    // Descrizione sotto il campo e non fra etichetta e campo: sopra spingeva giù l'input e
    // nelle griglie i campi della stessa riga non restavano allineati. Vale per tutti gli
    // input, che leggono i default di InputWrapper.
    InputWrapper: {
      defaultProps: {
        inputWrapperOrder: ['label', 'input', 'description', 'error'],
      },
    },
    // Numeri all'italiana: la virgola per i decimali. Il punto si può comunque digitare e
    // diventa virgola, così il tastierino numerico funziona. Le migliaia solo dove servono
    // (quantità, importi): su porte, serie e codici "2.525" sarebbe fuorviante.
    NumberInput: NumberInput.extend({
      defaultProps: {
        decimalSeparator: ',',
      },
    }),
    // Tutti i selettori di data: oggi evidenziato nel calendario.
    DatePickerInput: DatePickerInput.extend({
      defaultProps: {
        highlightToday: true
      },
    }),
  },
});

root.render(
  <React.StrictMode>
    <AppProvider>
      <MantineProvider theme={theme}>
        {/* Calendari in italiano, con la settimana che parte dal lunedì. */}
        <DatesProvider settings={{ locale: 'it', firstDayOfWeek: 1 }}>
          <Notifications autoClose={3500} />
          <RouterProvider router={router} />
        </DatesProvider>
      </MantineProvider>
    </AppProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
